/**
 * useDataTable.ts
 * ───────────────
 * Composable Vue 3 extrait de DataTableView.vue.
 * Contient toute la logique de chargement, filtrage, affichage et téléchargement
 * des données tabulaires.
 *
 * Usage dans la vue :
 *   setup(props) { return useDataTable(props); }
 */
import { computed, onMounted, onUpdated, provide, ref } from "vue";
import services from "@/composable/services";
import useArray from "@/composable/components/array";
import useBoolean from "@/composable/components/boolean";
import useNumber from "@/composable/components/number";
import useObject from "@/composable/components/object";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { LocalTag } from "@/types/front/LocalTag";
import { LocalChecker } from "@/types/front/LocalChecker";
import { buildTagsColumns } from "@/composable/application/tags";
import { checkMessageErrors, type ErrorType } from "@/composable/application/errors";
import { lineCountSynthesis } from "@/composable/application/synthesis";
import { dataLoader } from "@/composable/data/dataLoader";
import { patternCheckerDateRef } from "@/composable/application/datePattern";
import { addRefLinkedTo, buildFilterListMap, detectBrowserColumnStrategy } from "@/composable/data/dataTableUtils";
import { buildColumnsFromVariables, mergeAndSortColumns } from "@/composable/data/columnBuilder";
import { usePageLifecycle } from "@/composable/data/usePageLifecycle";
import { Fetcher } from "@/services/Fetcher";
import { Component, PatternComponent, PatternAdjacentComponent } from "@/types/front/LocalComponent";
import i18n from "@/i18n";
import router from "@/router";
import {
  Application,
  ComponentFilters,
  DataRowResult,
  DownloadDatasetQuery,
  FilterList,
  GetDataResult,
  OutPut,
  ValidationCheckResultRest,
} from "@/types/generated/UsedGeneratedTypes";

interface DataTableProps {
  applicationName: string;
  dataId: string;
}

export function useDataTable(props: DataTableProps) {
  // ─── State ────────────────────────────────────────────────────────────────

  const loadExample = ref(true);
  const state = ref("");
  const perPage = ref(20);

  const { reactiveObject: params } = useObject({
    application: {} as unknown as Application,
    offset: 0,
    limit: perPage.value,
    dataName: "",
    outPut: {} as unknown as OutPut,
    rowIds: [],
    naturalKeys: [],
    componentSelects: [],
    componentFilters: [],
    componentOrderBy: [],
    authorizationDescriptions: [],
    horizontalDisplay: false,
  } as DownloadDatasetQuery);

  const locale: string | null = services.internationalisationService.getLocale();
  const loader = dataLoader(services);
  provide("reference:dataLoader", loader);
  const { getColumnNameView, getValueDisplay } = loader;

  const { shallowRefArray: errorsMessages } = useArray();
  const { refNumber: currentPage, doChangeNumber: changeCurrentPage } = useNumber(1);
  const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean();
  const { refBoolean: horizontalDisplay, doChangeBoolean: changeHorizontalDisplay } = useBoolean();
  const canHorizontalize = ref(false);

  const { reactiveObject: application, doChangeObject: changeApplication } = useObject(new LocalApplicationResult());
  const { shallowRefArray: data, doChangeArray: changeData } = useArray();
  const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
  const { shallowRefArray: columns, doChangeArray: changeColumns } = useArray();
  const { shallowRefArray: rows, doChangeArray: changeRows } = useArray();
  const { shallowRefArray: tableValues, doChangeArray: changeTableValues } = useArray();
  const { reactiveObject: tags, doChangeObject: _changeTags, doAddToObject: _addToTags } = useObject();
  const { reactiveObject: referenceTypeForReferencingColumns } = useObject();
  const { reactiveObject: filterLists, doChangeObject: doAddFilterLists } = useObject();
  const { reactiveObject: displaysForRows } = useObject();
  const { reactiveObject: referencesDynamic, doChangeObject: changeReferencesDynamic } = useObject();
  const rowHeight = ref(2.67);

  const tableHeight = computed(() => {
    if (rows.value.length < perPage.value) {
      return "auto";
    }
    return rowHeight.value * 21 + "rem";
  });

  const MAX_ROWS_LIMIT = 500;
  // Safety net : si l'endpoint /filters ne résout JAMAIS ( backend hang ,
  // réseau drop , event loop bloqué ) , on force `filtersLoading=false`
  // après ce délai pour basculer l'UI sur le placeholder "no-criteria"
  // ou les filtres partiellement chargés au lieu d'une progressbar
  // infinie . Trade-off : au pire l'utilisateur voit ce délai avant
  // fallback . Valeur volontairement large , c'est un filet de sécurité .
  const FILTERS_LOAD_SAFETY_TIMEOUT_MS = 600_000;
  // Safety net symétrique pour `/data` ( init() ) : si le backend hang
  // ou met >60s à répondre ( pool Hikari saturé pendant UPSERT , SQL
  // lente sur grosse table , etc. ) , on bascule `isLoading=false` pour
  // ne pas laisser un spinner infini . Plus large que le timeout
  // filtres car /data peut légitimement durer plusieurs dizaines de
  // secondes sur gros datasets ( ~500 lignes × ~12 colonnes ) .
  const DATA_LOAD_SAFETY_TIMEOUT_MS = 300_000;
  const { refBoolean: isSup500 } = useBoolean(false);
  const { refBoolean: isData } = useBoolean(false);
  const { refBoolean: showPiece } = useBoolean(false);
  // PERF #465 - Etat de chargement des filtres (initialise a true pour afficher le spinner des le depart)
  const { refBoolean: filtersLoading } = useBoolean(true);
  // Cycle de vie de la page : 2 fetches initiaux + action de refresh
  // courante + flag d'interaction utilisateur . Voir usePageLifecycle.ts
  // pour la spec complete de la machine d'etat ; isole hors useDataTable
  // pour la testabilite + extensibilite ( ajout d'une action future =
  // 1 entree dans REFRESH_ACTIONS , aucun changement ici ) .
  const lifecycle = usePageLifecycle();
  const {
    filtersResolvedOnce,
    dataResolvedOnce,
    initialLoadComplete,
    refreshLabelKey: lifecycleRefreshLabelKey,
    hasUserInteracted,
    markFiltersResolved,
    markDataResolved,
    startRefresh,
    endRefresh,
  } = lifecycle;
  // Etat du bloc Filter en mode INITIAL : true tant que /filters n'a pas
  // resolu une fois . Decouple de /data depuis le sprint cache ( shape
  // /filters = {entries, variables} ) : columns.value est desormais
  // populee EARLY dans loadFiltersAsync.then() via buildColumnsFromVariables
  // avec les variables exposees par /filters . Le bloc filtre peut donc
  // se rendre des l'arrivee de /filters ( ~500ms cache miss , ~30ms hit )
  // sans attendre /data ( 5-30s sur gros datasets ) .
  //
  // Iso-source FilterListVariablesExtractor cote backend : aucune column
  // dans variables ne produira 'aucun filtre disponible' frontend , car
  // les components dans FilterListNode + ColumnDistinctValues viennent
  // du MEME calcul que variables .
  const filterBlockLoading = computed(() => !filtersResolvedOnce.value);
  // Filtres deja affiches + rechargement /data en cours ( clic Consulter
  // ou Reinitialiser apres init ) . Permet d'afficher une slim progressbar
  // overlay en bas du card-content SANS replace les filtres ni modifier
  // la hauteur/largeur du bloc .
  const filterBlockRefreshing = computed(
    () => initialLoadComplete.value && isLoading.value,
  );
  // Label affiche pendant un refresh . Renvoie la cle i18n de l'action
  // courante ( fournie par le lifecycle ) si on est effectivement en
  // refresh ; sinon fallback generique "filter.refreshing-data" pour
  // couvrir les flippes d'isLoading hors wrapper ( pagination ,
  // re-render synchrone ) . Renvoie null hors refresh .
  const refreshLabelKey = computed<string | null>(() => {
    if (!filterBlockRefreshing.value) return null;
    return lifecycleRefreshLabelKey.value ?? "filter.refreshing-data";
  });
  // #476 - Distinguer "pas encore charge" de "filtre applique avec 0 resultats"
  const { refBoolean: hasSearched } = useBoolean(false);
  // Spinner pendant le re-render synchrone du <b-table> au changement de
  // pagination ( jusqu'à 500 lignes × ~12 colonnes = ~6000 composants Vue
  // à instancier , Buefy ne virtualise pas ). Voir onPerPageChange.
  const { refBoolean: paginationLoading } = useBoolean(false);
  const { refBoolean: isSticky } = useBoolean(true);
  const { shallowRefArray: listReferenceValueForAllReferences } = useArray();

  /**
   * Bandeau "limite à 500 lignes" ( DataTableView ) : une fois fermé
   * par l'utilisateur , on retient l'état dans sessionStorage pour ne
   * plus le ré-afficher pendant la session en cours , quel que soit
   * le datatype consulté. Il revient à la prochaine session ( reload à
   * froid ou nouvelle connexion ).
   * sessionStorage choisi sur localStorage : signal éphémère propre à
   * la session de navigation , conforme au scope demandé par l'UX.
   */
  const INFO_500_DISMISS_KEY = "OA_DISMISS_INFO_500";
  const info500Dismissed = ref(sessionStorage.getItem(INFO_500_DISMISS_KEY) === "1");
  function dismissInfo500() {
    sessionStorage.setItem(INFO_500_DISMISS_KEY, "1");
    info500Dismissed.value = true;
  }

  const hasTags = useBoolean(false).refBoolean;
  const totalRows = ref(0);

  // ─── Colonnes calculées ───────────────────────────────────────────────────

  const realVariables = computed(() => {
    return (columns.value || []).reduce((acc: Component[], column: Component) => {
      if (column.type === "PatternComponent") {
        if (horizontalDisplay.value) {
          const row = rows.value[0];
          if (row && row.values && row.values[column.id]) {
            row.values[column.id]
              .map((value: unknown) => column.componentsForValue(value, row))
              .forEach((col: Component[]) => col.forEach((c: Component) => acc.push(c)));
          }
        }
        else {
          (column as PatternComponent).horizontalDisplay = false;
          acc.push(column);
          if (rows.value[0]) {
            (column as PatternComponent)
              .getColumnQualifiersMap(application, null, rows.value[0], horizontalDisplay.value)
              .forEach((qualifier: Component) => acc.push(qualifier));
          }
        }
      }
      else if (!(column instanceof PatternAdjacentComponent)) {
        acc.push(column);
      }
      return acc;
    }, []);
  });

  const dataColumnsToBeShown = computed((): Component[] | PatternComponent[] => {
    const strategy = detectBrowserColumnStrategy(navigator.userAgent);
    if (strategy === "firefox") {
      return services.tagService.toBeShownDataColumns(tags, realVariables.value);
    }
    if (strategy === "chromium") {
      return services.tagService.toBeShown(tags, realVariables.value);
    }
    console.warn("Navigateur non testé — ordre des filtres par défaut", navigator.userAgent);
    return services.tagService.toBeShown(tags, realVariables.value);
  });

  const dataColumnsForFilters = computed(() => {
    const colomnsForFilters = (columns.value || []).reduce((acc: Component[], column: Component) => {
      if (column.type === "PatternComponent") {
        (column as PatternComponent).horizontalDisplay = false;
        acc.push(column);
        (column as PatternComponent)
          .getColumnQualifiersMap(application, null, rows.value[0], horizontalDisplay.value)
          .forEach((qualifier: PatternComponent) => acc.push(qualifier));
      }
      else if (!(column instanceof PatternAdjacentComponent)) {
        acc.push(column);
      }
      return acc;
    }, []);

    const strategy = detectBrowserColumnStrategy(navigator.userAgent);
    if (strategy === "firefox") {
      return services.tagService.toBeShownDataColumns(tags, colomnsForFilters);
    }
    if (strategy === "chromium") {
      return services.tagService.toBeShown(tags, colomnsForFilters);
    }
    console.warn("Navigateur non testé — ordre des filtres par défaut", navigator.userAgent);
    return services.tagService.toBeShown(tags, colomnsForFilters);
  });

  // ─── Tags ─────────────────────────────────────────────────────────────────

  function changeTags(tagsToChange: object) {
    _addToTags(tagsToChange);
    hasTags.value
      = tags
        && Object.keys(tags || {}).length !== 0
        && !(
          Object.keys(tags || {}).length === 1
          && Object.keys(tags || {}).includes(LocalTag.NO_TAG_NAME)
        );
  }

  function changeValueSticky(event: boolean) {
    isSticky.value = event;
  }

  function changeAllValueTags(allTags: boolean) {
    for (const key in tags) {
      tags[key].selected = allTags;
    }
    changeTags(tags);
  }

  async function changeTagSelected(tag: { key: string; checked: boolean }) {
    if (tag.key !== undefined && Object.prototype.hasOwnProperty.call(tags, tag.key)) {
      tags[tag.key].selected = tag.checked;
      changeTags(tags);
    }
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  onMounted(async () => {
    await init();
    const dataIsType = application?.dataTypes?.[props.dataId] ? "dataTypes" : "references";
    changeSubMenuPaths([
      new SubMenuPath(
        application.dataTypes?.[props.dataId]
          ? i18n.global.t("dataTypesManagement.data-types").toLowerCase()
          : i18n.global.t("referencesManagement.references").toLowerCase(),
        () => router.push(`/applications/${props.applicationName}/${dataIsType}`),
        () => router.push("/applications"),
      ),
      new SubMenuPath(
        application.localRefName ? application.localRefName : props.dataId,
        () => router.push(`/applications/${props.applicationName}/${dataIsType}/${props.dataId}`),
        () => router.push(`/applications/${props.applicationName}/${dataIsType}`),
      ),
    ]);
    // @ts-expect-error TODO: pending proper type annotation
    changeColumns(services.tagService.toBeShown(tags, columns.value));
    // @ts-expect-error TODO: pending proper type annotation
    changeTags(buildTagsColumns(columns.value, tags));
  });

  onUpdated(() => {
    const nav = document.querySelector("nav.pagination");
    if (nav) {
      nav.classList.add("is-centered", "is-rounded");
    }
  });

  // ─── Initialisation ──────────────────────────────────────────────────────

  async function init() {
    changeIsLoading(true);
    // Safety net : si init() ne se termine JAMAIS ( promise rejetée
    // silencieusement , backend hang , event loop bloqué ) , on force
    // `isLoading=false` après DATA_LOAD_SAFETY_TIMEOUT_MS . Évite un
    // spinner infini côté UI quand le pool Hikari backend est saturé
    // pendant un UPSERT par exemple . Symétrique au safety timeout
    // sur `filtersLoading` dans loadFiltersAsync .
    const dataSafetyTimeout = setTimeout(() => {
      if (isLoading.value) {
        console.warn(`[useDataTable] init() safety timeout ${DATA_LOAD_SAFETY_TIMEOUT_MS}ms - forcing isLoading=false`);
        changeIsLoading(false);
        markDataResolved();
      }
    }, DATA_LOAD_SAFETY_TIMEOUT_MS);
    try {
      const fetchedApplication = await services.applicationService.getApplication(
        props.applicationName,
        ["CONFIGURATION", "REFERENCETYPE"],
      );
      if (fetchedApplication && locale) {
        fetchedApplication.localRefName
          = fetchedApplication.configuration?.i18n?.data?.[props.dataId]?.i18n?.title[locale]
            || props.dataId;
        fetchedApplication.localRefDescription
          = fetchedApplication.configuration?.i18n?.data?.[props.dataId]?.i18n?.description[locale]
            || props.dataId;
        changeApplication(fetchedApplication);
      }

      totalRows.value
        = application.referenceSynthesis?.find(
          (synthesis: { referenceType: string; lineCount: number }) =>
            synthesis.referenceType === props.dataId,
        )?.lineCount || 0;
      isSup500.value = totalRows.value >= MAX_ROWS_LIMIT;
      isData.value = !!application.dataTypes?.[props.dataId];

      if (isSup500.value && isData.value) {
        params.limit = MAX_ROWS_LIMIT;
      }
      else {
        params.limit = totalRows.value;
      }

      // PERF audit (8/5/26) - /filters et /data/json sont indépendants côté
      // backend ( deux endpoints distincts , deux requêtes SQL distinctes ).
      // Ils partagent le même pool Hikari main ( 30 connexions par défaut ,
      // largement suffisant pour 2 requêtes parallèles d'un même user ; le
      // streaming pool est réservé aux downloads ZIP/CSV ). On les tire en
      // parallèle plutôt que séquentiellement pour ne pas faire attendre
      // les filtres derrière le SELECT de la table ( ~5 s sur gros dataset ).
      // Le résultat /filters arrive quand il arrive et alimente filterLists
      // via doAddFilterLists ; le tableau s'affiche dès que rawData est résolu.
      loadFiltersAsync(props.applicationName, props.dataId);

      const rawData = await services.dataService.getData(
        props.applicationName,
        props.dataId,
        {
          ...params,
          horizontalDisplay: horizontalDisplay.value,
          offset: params.offset,
          limit: params.limit,
        },
        isSup500.value && isData.value,
      );

      canHorizontalize.value = rawData.patternDefinitionCount >= 1;
      await updateData(rawData, params.limit);
    }
    catch (error: unknown) {
      services.alertService.toastServerError(error as ErrorType, state);
      // Si init() throw AVANT loadFiltersAsync ( ligne 308 ) , `filtersLoading`
      // resterait a sa valeur initiale true ( useBoolean(true) ligne 105 ) ->
      // barre de progression infinie cote FiltersDataCollapse . Reset force ici .
      filtersLoading.value = false;
      markFiltersResolved();
    }
    finally {
      clearTimeout(dataSafetyTimeout);
      changeIsLoading(false);
      // Marquer "fetch /data resolu au moins une fois" ( succes ou
      // erreur catchee ci-dessus ) . Combine avec filtersResolvedOnce
      // dans `initialLoadComplete` pour basculer le bloc Filter du mode
      // initial ( overlay full ) au mode refresh ( overlay slim ) .
      markDataResolved();
    }
  }

  // ─── Chargement des données ───────────────────────────────────────────────

  async function setInitialVariables(variables: string[]) {
    // @ts-expect-error TODO: pending proper type annotation
    changeData(application?.configuration?.dataDescription?.[props.dataId]);
    if (!data) return;

    // #476 - Si le resultat est vide (0 variables), garder les colonnes existantes
    // pour que les filtres restent visibles et que l'utilisateur puisse modifier ses criteres
    if (!variables || variables.length === 0) return;

    // Délégation au helper pur ( buildColumnsFromVariables ) : single source
    // of truth pour la construction des Component depuis le config + variables .
    // Appelé aussi depuis loadFiltersAsync.then() pour populer columns.value
    // EARLY ( bloc filtre rendu dès /filters sans attendre /data ) .
    const built = buildColumnsFromVariables(
      application,
      props.dataId,
      new Set(variables),
      services,
    );
    const localColumns = mergeAndSortColumns(built);
    changeColumns(localColumns);

    if (rows.value) {
      changeTableValues(Object.values(rows.value || {}).map((refValue: DataRowResult) => refValue.values));
    }

    // PERF #465 — Chargement parallèle des colonnes dynamiques (références)
    // Avant : les appels étaient séquentiels (await dans un for) → ~3-5s pour 5-10 colonnes
    // Après : Promise.all() lance tous les appels en parallèle → ~0.5-1s
    //
    // Cette partie reste dans setInitialVariables ( pas extraite dans le helper )
    // car elle dépend de application.data?.[dataId] qui n'est rempli qu'après /data .
    const dynamicColumnsToLoad = columns.value.filter(
      (col: Component) => built.dynamicComponents[col?.id] && application.data?.[props.dataId],
    );
    if (dynamicColumnsToLoad.length > 0) {
      const dynamicResults = await Promise.all(
        dynamicColumnsToLoad.map((col: Component) =>
          services.dataService.getData(
            props.applicationName,
            // @ts-expect-error TODO: pending proper type annotation
            application.data?.[props.dataId].componentDescriptions[col.id].reference,
          ).then(result => ({ col, result })),
        ),
      );
      for (const { col, result } of dynamicResults) {
        changeReferencesDynamic(result);
        // @ts-expect-error referenceType is assigned at runtime
        built.dynamicComponents[col.id].referenceType
          = application.data?.[props.dataId].componentDescriptions[col.id].reference;
      }
    }
  }

  /**
   * PERF #465 — Charge les listes de filtres (dropdowns) de manière asynchrone.
   * Les données s'affichent immédiatement (~1-2s), puis cette fonction charge les
   * filtres en arrière-plan via le nouvel endpoint GET /filters.
   * Au 2ème appel dans les 10 minutes, le backend retourne le résultat depuis son cache.
   */
  function loadFiltersAsync(applicationName: string, dataId: string) {
    filtersLoading.value = true;
    // Safety net : si la Promise ne résout JAMAIS , `.finally()` ne sera
    // jamais appelé -> progressbar infinie côté FiltersDataCollapse . On
    // force `false` après FILTERS_LOAD_SAFETY_TIMEOUT_MS pour que l'UI
    // bascule sur le placeholder "no-criteria" ou les filtres partiels .
    const safetyTimeout = setTimeout(() => {
      if (filtersLoading.value) {
        console.warn(`[useDataTable] loadFiltersAsync safety timeout ${FILTERS_LOAD_SAFETY_TIMEOUT_MS}ms - forcing filtersLoading=false`);
        filtersLoading.value = false;
        markFiltersResolved();
      }
    }, FILTERS_LOAD_SAFETY_TIMEOUT_MS);
    services.dataService.getDataFilters(applicationName, dataId)
      .then((filtersResponse) => {
        // Le payload /filters expose desormais ( commit feat(filters): payload
        // /filters retourne {entries, variables} cote backend ) :
        //   - entries : FilterListEntry[] ( valeurs des dropdowns )
        //   - variables : string[] ( componentKeys filtrables , iso-source
        //                            backend - aucun 'aucun filtre disponible' )
        const entries = (filtersResponse?.entries || []) as FilterList[];
        doAddFilterLists(buildFilterListMap(entries));

        // EARLY populate columns.value des l'arrivee de /filters via le
        // helper buildColumnsFromVariables ( pure , teste 15 tests verts ) .
        // Le bloc filtre peut donc se rendre IMMEDIATEMENT sans attendre
        // /data ( gain UX gros datasets , ex ACBB 14M lignes 30s -> 0s ) .
        // Si /data resout APRES , setInitialVariables ( appele depuis
        // updateData ) re-build columns.value avec son propre set variables
        // ( idempotent si meme set , re-render Vue tracke les changements ) .
        const variables = filtersResponse?.variables;
        if (variables && variables.length > 0 && columns.value.length === 0) {
          const built = buildColumnsFromVariables(
            application,
            dataId,
            new Set(variables),
            services,
          );
          changeColumns(mergeAndSortColumns(built));
        }
      })
      .catch((error: unknown) => {
        console.warn("Failed to load filters asynchronously", error);
      })
      .finally(() => {
        clearTimeout(safetyTimeout);
        filtersLoading.value = false;
        // Marquer "fetch /filters resolu au moins une fois" - combine
        // avec dataResolvedOnce ( cote init() ) pour calculer
        // initialLoadComplete = filtersResolvedOnce && dataResolvedOnce .
        markFiltersResolved();
      });
  }

  async function updateData(tableValue: GetDataResult, limit: number) {
    changeRows(tableValue.rows);
    const variables = tableValue.variables;
    await setInitialVariables(variables);
    changeIsLoading(false);
    params.limit = limit;
  }

  // ─── Filtres & Recalcul ───────────────────────────────────────────────────

  async function removeTag(event: { filters: Array<{ componentKey: string; filters?: unknown[] }> }) {
    params.componentFilters = (params.componentFilters
      .filter((cf: ComponentFilters) => {
        const match = event.filters.find(f => f.componentKey === cf.componentKey);
        return !match || (Array.isArray(match.filters) ? match.filters.length > 0 : true);
      })
      .map((cf: ComponentFilters) => {
        const match = event.filters.find(f => f.componentKey === cf.componentKey);
        return match ? match : cf;
      })) as unknown as ComponentFilters[];
  }

  // #476 - Reinitialiser les filtres sans recharger la page
  // Avant : window.location.reload() provoquait un flash blanc et la perte de tout le contexte
  async function clear(_event: unknown) {
    params.componentFilters = [];
    hasSearched.value = false;
    changeCurrentPage(1);
    await recalculate(undefined, false, false, true);
  }

  /**
   * Wrappers contextualises pour la slim progressbar de refresh . Delegue
   * au lifecycle ( startRefresh / endRefresh ) qui positionne l'action
   * courante + bascule hasUserInteracted = true ; le label adapte
   * ( "Searching..." vs "Resetting filters..." ) est derive cote
   * FiltersDataCollapse via la prop {@link refreshLabelKey} .
   *
   * <p>Garde la signature des handlers existants pour eviter une refacto
   * des templates parents ( DataTableView ) .
   */
  async function searchData(event: { filters?: ComponentFilters[] } | undefined) {
    startRefresh("search");
    try {
      await recalculate(event, false, false, true);
    }
    finally {
      endRefresh();
    }
  }

  async function resetFilters(event: unknown) {
    startRefresh("reset");
    try {
      await clear(event);
    }
    finally {
      endRefresh();
    }
  }

  async function recalculate(
    event: { filters?: ComponentFilters[] } | undefined,
    shouldDownload?: boolean,
    isHorizontalizedOrVerticalized?: boolean,
    showResult?: boolean,
  ) {
    if (event) {
      event.filters = (event.filters || []).map((group: ComponentFilters) => {
        if (group.filters && Array.isArray(group.filters)) {
          if (group.filters.length !== 0) {
            group.filters = group.filters.map(
              (item: unknown) =>
                (item as { naturalKey?: { sql: string } })?.naturalKey?.sql || item,
            ) as string[];
          }
          else {
            return null as unknown as ComponentFilters;
          }
        }
        // #476 - Ne garder que les champs utiles au backend (componentKey + filters/intervalsValues)
        // Exclure l'objet columns qui alourdit inutilement la requete.
        //
        // #67 - {@code isRegExp} DOIT être propagé au backend pour que les
        // filtres __FILTER_TEXT__ soient routés vers
        // {@link ComponentFiltersForWordByRegexp} ( contains insensible
        // casse via {@code @ like_regex flag "i"} ). Sans ce flag , le
        // backend retombe sur {@link ComponentFiltersForWordByPlainText}
        // ( égalité stricte ) et la recherche partielle ne matche plus
        // ( ex: chercher "Regia" ne trouvait pas "Aqua Regia" ).
        const clean: Record<string, unknown> = { componentKey: group.componentKey };
        if (group.filters) clean.filters = group.filters;
        if ((group as any).intervalsValues) clean.intervalsValues = (group as any).intervalsValues;
        if ((group as any).isRegExp) clean.isRegExp = (group as any).isRegExp;
        return clean as unknown as ComponentFilters;
      });
      event.filters = event.filters.filter((value: unknown) => value !== null);
    }

    isLoading.value = true;
    loadExample.value = false;
    changeCurrentPage(1);

    if (shouldDownload) {
      services.alertService.info(
        i18n.global.t("dataVersioning.modal-dialog-information.title"),
        i18n.global.t("dataVersioning.modal-dialog-information.message-extraction"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-validate"),
        i18n.global.t("dataVersioning.modal-dialog-information.btn-delete"),
        24000,
      );
      await downloadResultSearch(event);
    }
    else if (isHorizontalizedOrVerticalized || showResult) {
      const limit = params.limit;
      if (isHorizontalizedOrVerticalized) {
        params.limit = limit || MAX_ROWS_LIMIT;
      }
      if (showResult) {
        params.limit = MAX_ROWS_LIMIT;
        params.componentFilters = pickActiveFilters(event?.filters)
          ?? params.componentFilters;
      }
      params.horizontalDisplay = horizontalDisplay.value;
      const tableValue = await services.dataService.getData(
        props.applicationName,
        props.dataId,
        params,
        isSup500.value && isData.value,
      );
      changeIsLoading(true);
      await updateData(tableValue, limit);
      params.limit = limit;
    }

    if (tableValues.value.length !== MAX_ROWS_LIMIT) {
      isSup500.value = tableValues.value.length >= MAX_ROWS_LIMIT;
    }
    // #476 - Marquer qu'une recherche avec filtres a ete effectuee
    hasSearched.value = true;
    isLoading.value = false;
  }

  // ─── Téléchargement ───────────────────────────────────────────────────────

  async function downloadResultSearch(event: { filters?: ComponentFilters[] } | undefined) {
    loadExample.value = false;
    // limit: 10 retire pour telecharger toutes les lignes au lieu d'etre limite a 10
    // const localParams = { offset: 0, limit: 10 } as DownloadDatasetQuery;
    const localParams = { offset: 0 } as DownloadDatasetQuery;
    localParams.componentFilters = pickActiveFilters(event?.filters)
      ?? params.componentFilters;

    const zipFileUrl = await services.dataService.getDataZip(
      props.applicationName,
      props.dataId,
      localParams,
    );

    const response = await fetch(zipFileUrl, {
      method: "GET",
      headers: { Authorization: `Bearer ${Fetcher.AUTHORIZATION?.value ?? ""}` },
    });
    if (!response.ok) throw new Error("Échec du téléchargement");

    let filename = `${props.applicationName}-${props.dataId}.zip`;
    const disposition = response.headers.get("Content-Disposition");
    if (disposition?.includes("filename=")) {
      const matches = disposition.match(/filename="?([^"]+)"?/);
      if (matches?.[1]) filename = matches[1];
    }

    const blob = await response.blob();
    const urlBlob = URL.createObjectURL(blob);
    const hiddenElement = document.createElement("a");
    hiddenElement.href = urlBlob;
    hiddenElement.download = filename;
    document.body.appendChild(hiddenElement);
    hiddenElement.click();
    hiddenElement.remove();
    URL.revokeObjectURL(urlBlob);
  }

  // ─── Suppression ─────────────────────────────────────────────────────────

  function askDeletionConfirmation(rowId: string) {
    services.alertService.confirm(
      i18n.global.t("alert.warning"),
      i18n.global.t("alert.reference-deletion-msg", { label: rowId }),
      i18n.global.t("alert.delete"),
      "is-danger",
      i18n.global.t("alert.cancel"),
      () => deleteRowReference(rowId),
    );
  }

  async function deleteRowReference(rowId: string) {
    try {
      await services.dataService.deleteReferenceValuesByKey(
        props.applicationName,
        // @ts-expect-error TODO: pending proper type annotation
        data.value.label,
        rowId,
      );
      services.alertService.toastSuccess(i18n.global.t("alert.reference-updated"));
    }
    catch (errors: unknown) {
      const errList: ValidationCheckResultRest[] = [];
      await checkMessageErrors(errors as ErrorType, errList, errorsMessages.value);
    }
  }

  // ─── Divers ───────────────────────────────────────────────────────────────

  function getRefColumnId(row: DataRowResult, column: Component): string | undefined {
    if (column.referenceType === "ReferenceChecker") {
      const refLinkedTo: string = column.refLinkedTo;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let refsLinkedToElement: any = row?.refsLinkedTo?.[refLinkedTo];
      let refLinkedToColumn: string | undefined = "";
      if (column.refLinkedToColumn && refsLinkedToElement?.[refLinkedToColumn as string]) {
        refLinkedToColumn = column.refLinkedToColumn;
        refsLinkedToElement = refsLinkedToElement?.[refLinkedToColumn]?.[0];
      }
      else {
        refLinkedToColumn = Object.keys(refsLinkedToElement || {}).find(key =>
          key.match(".*::(.*)::.*"),
        );
        if (refLinkedToColumn) {
          refsLinkedToElement = refsLinkedToElement?.[refLinkedToColumn]?.hierarchicalKey?.sql;
        }
      }
      return refsLinkedToElement as string | undefined;
    }
  }

  function resolveRefLinkedTo(idColumn: string, refLinkedTo: string | null | undefined) {
    return addRefLinkedTo(idColumn, refLinkedTo ?? null, referenceTypeForReferencingColumns);
  }

  async function changeDisplay(value: boolean) {
    changeHorizontalDisplay(value);
    await recalculate(undefined, false, true);
  }

  async function showModal() {
    // TODO: implement modal
  }

  /**
   * Conserve uniquement les filtres "actifs" pour l'envoi côté serveur :
   * un filtre est actif s'il porte au moins une valeur saisie (chip de
   * texte ajoutée , option de liste sélectionnée , borne d'intervalle
   * remplie ). Une colonne marquée filtrable mais que l'utilisateur n'a
   * pas renseignée doit être omise du payload pour que le backend
   * retourne le dataset complet ( décision Q6 de FILTER_TEXT_LIST.md ).
   *
   * <p>Retourne {@code undefined} si l'argument lui-même est nullish ;
   * les callers font alors un fallback vers les filtres déjà stockés
   * dans {@code params.componentFilters} ( cas du téléchargement initial ,
   * sans event ).
   */
  function pickActiveFilters(eventFilters: any): any {
    if (!Array.isArray(eventFilters)) return undefined;
    return eventFilters.filter((f: any) => {
      if (f == null) return false;
      // Un filtre est considéré actif s'il a au moins une valeur ;
      // on couvre les trois formes connues :
      //   - filters[] non vide ( PlainText / Reference / List )
      //   - intervalsValues avec au moins une borne ( Date / Numeric )
      //   - simpleValue défini ( Boolean )
      const hasFilters = Array.isArray(f.filters) && f.filters.length > 0;
      const hasInterval = Array.isArray(f.intervalsValues)
        && f.intervalsValues.some((iv: any) => iv?.from != null || iv?.to != null);
      const hasSimple = f.simpleValue !== undefined && f.simpleValue !== null && f.simpleValue !== "";
      return hasFilters || hasInterval || hasSimple;
    });
  }

  /**
   * Gère le changement du sélecteur "lignes par page".
   *
   * <p>Le re-render de {@code <b-table>} ( Buefy , sans virtualisation ) est
   * synchrone : passer de 20 à 500 lignes bloque le thread principal le
   * temps d'instancier les composants de cellule pour toutes les lignes.
   * Sans intervention , Vue ne paint pas le spinner avant ce blocage car
   * la mise à jour réactive est mise en lot avec celle de {@code perPage}.
   *
   * <p>Solution : interposer deux {@code requestAnimationFrame} entre la
   * levée du flag et la mise à jour effective , pour forcer le navigateur
   * à peindre le spinner avant le travail bloquant. Deux frames de plus
   * après la mise à jour laissent {@code <b-table>} terminer son patch
   * DOM avant qu'on ne retire le spinner.
   */
  async function onPerPageChange(newValue: number) {
    paginationLoading.value = true;
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    perPage.value = newValue;
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
    paginationLoading.value = false;
  }

  const MANY = LocalChecker.MULTIPLICITY_MANY;

  // ─── Retour ───────────────────────────────────────────────────────────────

  return {
    // State
    perPage,
    state,
    loadExample,
    realVariables,
    params,
    currentPage,
    errorsMessages,
    tableValues,
    referencesDynamic,
    isLoading,
    columns,
    rows,
    application,
    data,
    dataColumnsToBeShown,
    dataColumnsForFilters,
    MANY,
    hasTags,
    referenceTypeForReferencingColumns,
    tags,
    subMenuPaths,
    totalRows,
    tableHeight,
    loader,
    canHorizontalize,
    horizontalDisplay,
    isSup500,
    info500Dismissed,
    dismissInfo500,
    isData,
    isSticky,
    showPiece,
    listReferenceValueForAllReferences,
    filterLists,
    filtersLoading,
    filterBlockLoading,
    filterBlockRefreshing,
    refreshLabelKey,
    hasUserInteracted,
    paginationLoading,
    hasSearched,
    displaysForRows,
    locale,
    // Actions
    getRefColumnId,
    addRefLinkedTo: resolveRefLinkedTo,
    changeValueSticky,
    changeAllValueTags,
    changeTagSelected,
    recalculate,
    searchData,
    resetFilters,
    askDeletionConfirmation,
    removeTag,
    clear,
    changeDisplay,
    showModal,
    onPerPageChange,
    // Helpers
    lineCountSynthesis,
    getColumnNameView,
    getValueDisplay,
    patternCheckerDateRef,
  };
}
